import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ChartBar,
  Briefcase,
  Gear,
  Table,
  ArrowsLeftRight,
  ListChecks,
  ArrowClockwise,
  ClockClockwise,
  Heartbeat,
  SignOut,
  Database,
  FileMagnifyingGlass,
  Globe,
  Trash,
  Users,
  Robot,
  ChartLineUp,
} from "@phosphor-icons/react";

const API = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api", withCredentials: true });

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: ChartBar, roles: ["admin", "operator"] },
  { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["admin", "operator"] },
  { to: "/review", label: "Review Queue", icon: FileMagnifyingGlass, roles: ["admin", "operator"] },
  { to: "/unresolved", label: "Unresolved Queue", icon: ArrowClockwise, roles: ["admin", "operator"] },
  { to: "/trash", label: "Trash", icon: Trash, roles: ["admin"] },
  { type: "divider", label: "Settings", roles: ["admin"] },
  { to: "/settings/sites", label: "Sites", icon: Globe, roles: ["admin"] },
  { to: "/settings/sheets", label: "Sheet Registry", icon: Table, roles: ["admin"] },
  { to: "/settings/panels", label: "Panel Sources", icon: Database, roles: ["admin"] },
  { to: "/settings/mappings", label: "Field Mappings", icon: ArrowsLeftRight, roles: ["admin"] },
  { to: "/settings/rules", label: "Matching Rules", icon: ListChecks, roles: ["admin"] },
  { type: "divider", label: "System", roles: ["admin", "operator"] },
  { to: "/ai-assistant", label: "AI Asisten", icon: Robot, roles: ["admin", "operator"] },
  { to: "/ai-audit", label: "AI Audit", icon: ChartLineUp, roles: ["admin"] },
  { to: "/audit", label: "Audit Log", icon: ClockClockwise, roles: ["admin"] },
  { to: "/users", label: "User Management", icon: Users, roles: ["admin"] },
  { to: "/health", label: "System Health", icon: Heartbeat, roles: ["admin", "operator"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [trashCount, setTrashCount] = useState(0);
  const [issuesSummary, setIssuesSummary] = useState({ jobs_with_issues: 0, total_not_found: 0, total_outflow: 0 });

  const fetchTrashCount = useCallback(async () => {
    try {
      const { data } = await API.get("/jobs/trash/list?limit=1&skip=0");
      setTrashCount(data.total || 0);
    } catch {}
  }, []);

  const fetchIssuesSummary = useCallback(async () => {
    try {
      const { data } = await API.get("/jobs/issues-summary");
      setIssuesSummary(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchTrashCount();
    fetchIssuesSummary();
    const interval = setInterval(() => {
      fetchTrashCount();
      fetchIssuesSummary();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTrashCount, fetchIssuesSummary]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filtered = NAV_ITEMS.filter((item) => item.roles.includes(user?.role));

  return (
    <aside
      data-testid="sidebar"
      className="w-56 min-h-screen bg-zinc-900 text-zinc-300 flex flex-col flex-shrink-0"
    >
      <div className="p-4 border-b border-zinc-800">
        <h2 className="text-sm font-semibold text-white tracking-tight font-heading">
          Rekonsiliasi
        </h2>
        <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mt-0.5">
          Data Platform
        </p>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto">
        {filtered.map((item, i) =>
          item.type === "divider" ? (
            <div
              key={i}
              className="px-4 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-600"
            >
              {item.label}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-zinc-800 text-white border-l-2 border-white"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50 border-l-2 border-transparent"
                }`
              }
            >
              <item.icon size={16} weight="regular" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/jobs" && issuesSummary.jobs_with_issues > 0 && (
                <span
                  data-testid="nav-jobs-issues-badge"
                  title={`${issuesSummary.jobs_with_issues} job dengan issue · ${issuesSummary.total_not_found} not found · ${issuesSummary.total_outflow} outflow`}
                  className="flex items-center gap-1"
                >
                  <span className="relative inline-flex">
                    <span className="absolute inline-flex h-2 w-2 rounded-full bg-red-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  <span className="text-[10px] font-mono bg-red-600 text-white px-1.5 py-0.5 rounded-sm min-w-[20px] text-center">
                    {issuesSummary.jobs_with_issues}
                  </span>
                </span>
              )}
              {item.to === "/trash" && trashCount > 0 && (
                <span className="text-[10px] font-mono bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded-sm min-w-[20px] text-center">
                  {trashCount}
                </span>
              )}
            </NavLink>
          )
        )}
      </nav>

      <div className="border-t border-zinc-800 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-sm bg-zinc-700 flex items-center justify-center text-xs font-semibold text-white">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-zinc-500 font-mono uppercase">{user?.role}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors w-full"
        >
          <SignOut size={14} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
